import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Calendar, Mail, Phone } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";

const PATH = "/terms";

const LAST_UPDATED = "February 2026";

// ============================================================================
// CONTENT
// ============================================================================

const SECTIONS = [
  {
    id: "general",
    number: "1",
    title: "General Terms",
    subsections: [
      {
        id: "use-of-website",
        title: "1.1 Use of Website",
        paragraphs: [
          "Use of our website is subject to these Terms and Conditions.",
          "You may not misuse our website or use it for any fraudulent, unlawful, or unauthorized purposes.",
        ],
      },
      {
        id: "service-coverage",
        title: "1.2 Service Coverage",
        paragraphs: [
          "Our services are currently available only within London.",
          "Service availability may vary depending on location and operational capacity.",
        ],
      },
      {
        id: "booking-and-quotations",
        title: "1.3 Booking and Quotations",
        paragraphs: [
          "Bookings may be made via telephone, email, live chat, or through our online booking system.",
          "All quotations are provided based on standard completion times and the information supplied at the time of booking.",
          "Final pricing may be adjusted where the scope of work changes, including but not limited to additional labour, materials, time requirements, or unforeseen circumstances.",
        ],
      },
      {
        id: "force-majeure",
        title: "1.4 Force Majeure",
        paragraphs: [
          "We shall not be held liable for any failure or delay in performance resulting from events beyond our reasonable control. Such events include, but are not limited to, severe weather conditions, natural disasters, road closures, industrial action, illness, pandemics, accidents, or other unforeseen events.",
          "In such circumstances, services may be postponed, rescheduled, or cancelled at the earliest reasonably convenient time without liability.",
        ],
      },
      {
        id: "customer-conduct",
        title: "1.5 Customer Conduct and Safety",
        paragraphs: [
          "We reserve the right to cancel or terminate a booking immediately where any employee, contractor, or operative feels unsafe or is subjected to harassment, threatening behaviour, aggression, abuse, or inappropriate conduct by the customer or any person present on-site.",
          "In such cases, the service shall be deemed forfeited and no refunds will be issued. We also reserve the right to refuse or restrict future bookings from the customer.",
        ],
      },
      {
        id: "pets",
        title: "1.6 Pets Policy",
        paragraphs: [
          "Customers are responsible for ensuring that all pets are properly secured during the provision of services to prevent interference, disruption, injury, or accidents.",
          "PTC shall not be held liable for any incidents, damages, injuries, delays, or additional cleaning requirements resulting from pet behaviour during or after the service.",
        ],
      },
      {
        id: "access-lockout",
        title: "1.7 Access and Lockout Policy",
        paragraphs: [
          "Customers are responsible for providing safe and timely access to the property at the scheduled appointment time.",
          "Where access cannot be gained, our team will wait for up to 45 minutes from the agreed appointment time. Following this period, the booking may be cancelled, rescheduled, or treated as a late cancellation at PTC's discretion, and applicable charges may apply.",
        ],
      },
      {
        id: "data-protection",
        title: "1.8 Data Protection",
        paragraphs: [
          "We are committed to protecting your personal information in accordance with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018, and all other applicable UK data protection laws.",
          "Any personal information collected through bookings, enquiries, communications, or use of our services shall be used solely for the purposes of service delivery, administration, customer support, billing, and related operational requirements.",
          "We do not sell, rent, or share customer information with third parties except where required by law, necessary for the provision of services, or where explicit customer consent has been obtained.",
          "Customers have the right to request access to their personal data, request correction of inaccurate information, request deletion of their data where applicable, and exercise any other rights available under applicable data protection legislation.",
          "Requests relating to personal data may be submitted to PTC using the contact details provided on the website.",
        ],
      },
    ],
  },
  {
    id: "pricing",
    number: "2",
    title: "Pricing and Payments",
    subsections: [
      {
        id: "pricing-details",
        title: "2.1 Pricing",
        paragraphs: [
          "All prices are based on average property sizes, standard property conditions, and the information provided at the time of booking.",
          "Quoted prices may vary depending on factors including, but not limited to, the size of the property, level of soiling, accessibility, additional labour requirements, or the time required to complete the service.",
          "Additional charges may apply for extended services, specialist requests, or properties considered larger or more complex than average.",
        ],
      },
      {
        id: "additional-charges",
        title: "2.2 Additional Charges",
        paragraphs: [
          "Any parking fees, congestion charges, toll charges, permit costs, or similar travel-related expenses incurred during the provision of services shall be the responsibility of the customer unless otherwise agreed in writing.",
          "Additional fees may also apply for the handling of delicate, hazardous, oversized, or specialist items, as well as for the use of specialist equipment, materials, or additional operatives where required.",
        ],
      },
      {
        id: "payment-terms",
        title: "2.3 Payment Terms",
        paragraphs: [
          "Unless otherwise agreed in writing, full payment is due upon the arrival of our team at the property and prior to the commencement of any services.",
          "We reserve the right to refuse, postpone, or cancel any booking where payment has not been received, authorised, or otherwise confirmed in advance.",
          "Accepted payment methods may be specified on our website or communicated at the time of booking.",
        ],
      },
    ],
  },
  {
    id: "service-terms",
    number: "3",
    title: "Service Terms",
    subsections: [
      {
        id: "service-performance",
        title: "3.1 Service Performance",
        paragraphs: [
          "We aim to provide all services with reasonable care, skill, and professionalism and to attend appointments at the scheduled time wherever reasonably possible.",
          "Any arrival times provided are estimates only and may be affected by traffic conditions, weather, operational delays, or other circumstances outside of our control.",
          "Delays caused by external or unforeseen factors shall not entitle the customer to a refund, compensation, or cancellation without charge.",
        ],
      },
      {
        id: "customer-responsibilities",
        title: "3.2 Customer Responsibilities",
        paragraphs: [
          "Customers are responsible for ensuring that our team has safe and adequate access to the property, including access to electricity, running hot water, and any other utilities reasonably required to perform the service.",
          "For end of tenancy cleaning services, all personal belongings, furniture items not included in the service, and general clutter must be removed from the property prior to commencement of the service. This requirement does not apply to one-off deep cleaning services unless otherwise agreed.",
          "Customers also agree to empty cupboards, drawers, cabinets, and similar storage areas prior to the service where internal cleaning of such areas is requested.",
          "For end of tenancy cleaning services, properties must be vacant and free from occupants during the service unless otherwise agreed in advance.",
        ],
      },
      {
        id: "service-limitations",
        title: "3.3 Service Limitations",
        paragraphs: [
          "While we make reasonable efforts to achieve satisfactory results, we do not guarantee the removal of all stains, marks, odours, mould, limescale, or other conditions resulting from age, wear, neglect, or pre-existing damage.",
          "We are not responsible for the restoration, repair, or replacement of pre-existing damage, deterioration, discolouration, or faulty installations.",
          "Specialist services including, but not limited to, appliance deep cleaning, wall washing, biohazard cleaning, extensive mould treatment, pest-related cleaning, or restoration work must be specifically requested and separately booked, and may incur additional charges.",
        ],
      },
      {
        id: "after-builders",
        title: "3.4 After Builders Cleaning",
        paragraphs: [
          "Customers must ensure that all major construction, renovation, or building work has been completed prior to our team's arrival and that the property is safe, accessible, and suitable for cleaning.",
          "The property must be free from contractors, construction personnel, active renovation work, tools, machinery, and building materials during the cleaning service.",
          "The removal of heavy waste, rubble, plasterboard, building debris, hazardous materials, or construction refuse is not included unless specifically agreed in advance as part of the booking.",
          "Paint, plaster, cement, silicone, grout haze, adhesives, and similar construction residues will be treated with reasonable care. However, we do not guarantee complete removal where surfaces are delicate, porous, damaged, improperly sealed, or where residues have hardened or bonded deeply to surfaces. Specialist residue removal services may require separate booking and additional charges.",
          "Customers must disclose any specialist materials, high-value finishes, delicate surfaces, natural stone, untreated wood, bespoke installations, or manufacturer care restrictions at the time of booking to allow appropriate cleaning methods to be considered.",
          "Wall cleaning is not included unless specifically booked as a separate service.",
          "Customers are responsible for ensuring that electricity, hot water, lighting, ventilation, and other necessary utilities are functional on the day of service.",
          "For any re-clean request or guarantee to be considered, the property must remain unused, unoccupied, and unaltered following completion of the initial service. Any additional building work, decorating, repairs, contractor visits, or occupancy after the service shall void any re-clean guarantee.",
        ],
      },
      {
        id: "carpet-upholstery",
        title: "3.5 Carpet and Upholstery Cleaning",
        paragraphs: [
          "PTC shall not be held liable for carpet shrinkage, lifting, loosening, or distortion resulting from poor fitting, pre-existing installation issues, age-related deterioration, or manufacturer defects.",
          "PTC shall not be responsible for wear, shading, pile distortion, bleaching, discoloration, fabric weakness, or pre-existing damage that becomes more visible following the removal of dirt, staining, or residues.",
          "Carpet cleaning services booked in conjunction with end of tenancy cleaning are excluded from any 7-day re-clean guarantee unless expressly stated otherwise.",
          "We shall not be liable for any damage, staining, colour transfer, or marking resulting from furniture or other objects being placed on damp carpets or upholstery following cleaning.",
          "While we make reasonable efforts to treat visible stains and odours, PTC does not guarantee the removal of all stains, discoloration, pet odours, dye marks, rust marks, tannin stains, chemical spills, paint, or other permanent or pre-existing conditions.",
          "Customers must inform PTC at the time of booking if carpets, rugs, or upholstery contain natural fibres or specialist materials, including but not limited to wool, hessian-backed carpets, silk, viscose, cotton blends, or hand-finished fabrics.",
          "Customers are responsible for removing small furniture, valuables, breakables, electronics, ornaments, personal belongings, and other movable items prior to cleaning. PTC shall not be liable for damage except where directly caused by our negligence or failure to exercise reasonable care.",
          "Unless otherwise agreed in advance, large stationary items including televisions, fitted furniture, wardrobes, bookcases, closets, cupboards, and similarly immovable items will not be moved, and areas beneath or behind such items may not be cleaned.",
          "We make reasonable efforts to identify and treat visible staining; however, certain stains and conditions may be permanent due to the nature of the substance, prior cleaning attempts, DIY spotting agents, bleaching, chemical reactions, or fibre damage.",
          "PTC accepts no liability for colour run, dye migration, texture changes, or material reactions caused by manufacturer defects, unstable dyes, improper prior treatments, or non-colourfast fabrics.",
          "Carpets and upholstery are cleaned using professional equipment and industry-standard methods. However, complete drying, full odour elimination, or immediate usability cannot be guaranteed. Customers are advised to maintain adequate ventilation, airflow, and heating for a minimum of 24 hours following the service, particularly during cold, humid, or damp weather conditions.",
        ],
      },
      {
        id: "photos",
        title: "3.6 Photos for Quality Assurance",
        paragraphs: [
          "Our employees, contractors, or operatives may take time-stamped photographs of relevant work areas before, during, and after completion of services for quality assurance, training, health and safety compliance, record keeping, and dispute resolution purposes.",
          "Such photographs shall be stored securely and used solely for legitimate business purposes, including internal review processes or where reasonably necessary to investigate or resolve customer queries, complaints, claims, or disputes.",
          "Where reasonably possible, photographs will avoid capturing sensitive personal information or identifiable personal items unrelated to the service being provided.",
        ],
      },
    ],
  },
  {
    id: "cancellations",
    number: "4",
    title: "Cancellations and Refunds",
    subsections: [
      {
        id: "cancellation-policy",
        title: "4.1 Cancellation Policy",
        paragraphs: [
          "Customers may cancel a booking free of charge provided that notice is given at least 24 hours prior to the scheduled appointment time.",
          "Cancellations made within 24 hours of the scheduled service time shall incur a cancellation fee equal to 30% of the total booking value.",
          "We reserve the right to retain any deposits, booking fees, or advance payments to cover administrative costs, scheduling losses, or reserved operational time where applicable.",
        ],
      },
      {
        id: "rescheduling",
        title: "4.2 Rescheduling",
        paragraphs: [
          "Bookings may be rescheduled without charge where at least 24 hours' notice is provided prior to the scheduled appointment time.",
          "Rescheduling requests made within 24 hours of the appointment may be subject to availability and may incur additional charges at PTC's discretion.",
        ],
      },
      {
        id: "refunds",
        title: "4.3 Refunds",
        paragraphs: [
          "The customer acknowledges and agrees that the primary remedy for any complaint relating to the quality or completeness of the service shall be a complimentary re-clean, where PTC considers such a remedy appropriate.",
          "Where a complimentary re-clean is offered, the customer must provide reasonable access and opportunity for PTC to rectify the reported issues.",
          "If a customer refuses a reasonable re-clean offer for any reason, the customer agrees that they waive any entitlement to a full or partial refund and may instead become eligible for a discretionary refund of up to a maximum of 10% of the total booking value.",
        ],
        lists: [
          {
            heading: "A full refund shall only be considered under the following circumstances:",
            items: [
              "The service was cancelled by PTC (Prime Tenancy Cleaning);",
              "The cleaning team failed to attend the scheduled appointment without reasonable notice or attempted resolution; or",
              "The cleaning team attended but failed to complete at least 75% of the agreed services as listed on the original invoice, supported by clear and reasonable visual evidence.",
            ],
            footer: "In all other circumstances, full refunds shall not be offered.",
          },
          {
            heading: "Partial refunds may only be considered where:",
            items: [
              "A re-clean has first been arranged and completed;",
              "The customer remains dissatisfied with specific areas within the agreed service scope; and",
              "Sufficient visual evidence is provided demonstrating that certain agreed areas remain incomplete or below reasonable standard.",
            ],
            footer:
              "Any partial refund approved shall be proportionate to the value of the affected service areas as itemised on the original booking or invoice and shall not exceed the value of the disputed work.",
          },
          {
            heading: "Any re-clean request, complaint, or refund request shall be subject to the following conditions:",
            items: [
              "The complaint must be submitted in writing within 7 days of the original service date;",
              "The property must not have been re-occupied, altered, damaged, or cleaned by another contractor following completion of the original service;",
              "Full payment for the original booking must have been received; and",
              "The customer must have complied with all applicable service terms, including providing adequate access, utilities, and a safe working environment.",
            ],
          },
        ],
        trailing: [
          "PTC reserves the right to investigate all complaints and claims and to make a final determination based on available evidence, including photographs, videos, service records, operative reports, time logs, and customer communications.",
          "Where a refund is approved, it shall be calculated solely based on the portion of the service reasonably determined to have been incomplete or below standard, and not on the total booking value, unless expressly stated otherwise in these Terms and Conditions.",
        ],
      },
    ],
  },
  {
    id: "liability",
    number: "5",
    title: "Damage and Liability",
    subsections: [
      {
        id: "insurance",
        title: "5.1 Insurance",
        paragraphs: [
          "PTC maintains public liability insurance with cover of up to £1,000,000, subject to the terms, conditions, exclusions, and limitations of the applicable insurance policy.",
          "Proof of insurance may be provided upon reasonable request.",
        ],
      },
      {
        id: "damage-claims",
        title: "5.2 Damage Claims",
        paragraphs: [
          "Any claim relating to alleged loss or damage arising from the provision of services must be reported to PTC in writing within 48 hours of completion of the service.",
          "Claims submitted after this period may be rejected where delayed reporting prevents reasonable investigation or verification of the alleged damage.",
          "Customers must provide reasonable supporting evidence, including photographs, descriptions of the alleged damage, and access where necessary for inspection.",
        ],
        lists: [
          {
            heading:
              "Where PTC accepts responsibility for valid damage caused directly by our negligence, we may, at our discretion:",
            items: [
              "Arrange for repair of the affected item or area;",
              "Provide replacement where repair is not reasonably possible; or",
              "Offer compensation based on the item's current market value, age, condition, depreciation, and reasonable proof of ownership.",
            ],
            footer:
              "PTC shall not be liable for compensation exceeding the reasonable current value of the affected item.",
          },
        ],
      },
      {
        id: "exclusions",
        title: "5.3 Exclusions",
        paragraphs: [
          "PTC shall not be held liable for any damage, loss, delays, or failures arising from faulty equipment, unsuitable materials, defective installations, or products supplied by the customer or third parties.",
          "We accept no liability for pre-existing damage, wear and tear, deterioration, corrosion, structural defects, hidden defects, poor workmanship by third parties, or conditions existing prior to commencement of the service.",
          "PTC shall not be responsible for sentimental items, antiques, collectibles, irreplaceable items, cash, jewellery, documents, artwork, or other high-value personal belongings unless such liability is required by law and directly caused by proven negligence.",
          "Customers are responsible for securing and safely storing valuables and fragile items prior to the commencement of services.",
        ],
      },
    ],
  },
  {
    id: "guarantee",
    number: "6",
    title: "Guarantee and Complaints",
    subsections: [
      {
        id: "satisfaction-guarantee",
        title: "6.1 Satisfaction Guarantee",
        paragraphs: [
          "We offer a satisfaction guarantee on eligible services subject to the terms outlined in this agreement.",
          "End of tenancy cleaning services are covered by a 7-day re-clean guarantee.",
          "One-off deep cleaning and after builders cleaning services are covered by a 3-day re-clean guarantee.",
          "Any re-clean offered under this guarantee shall be limited to one re-clean visit per booking and shall be subject to inspection, assessment, and approval by PTC.",
          "The guarantee applies only to areas and services originally included within the confirmed booking and invoice.",
        ],
        lists: [
          {
            heading: "The guarantee shall become void where:",
            items: [
              "The property has been re-occupied, used, altered, or cleaned by another party after completion of the original service;",
              "Additional work, renovations, decorating, maintenance, or contractor activity has taken place following the original service;",
              "The customer refuses reasonable access for inspection or re-clean purposes; or",
              "The original booking has not been paid in full.",
            ],
          },
        ],
      },
      {
        id: "complaint-procedure",
        title: "6.2 Complaint Procedure",
        paragraphs: [
          "All complaints, concerns, or service disputes must be submitted to PTC in writing within 7 days of the original service date.",
        ],
        lists: [
          {
            heading: "Complaints must include:",
            items: [
              "A clear written description of the issue;",
              "Supporting photographic or video evidence where applicable; and",
              "Sufficient information to identify the booking and affected areas.",
            ],
          },
        ],
        trailing: [
          "PTC does not formally process or resolve complaints via telephone calls, verbal discussions, social media messaging, or live chat platforms.",
          "Upon receipt of a written complaint, the matter shall be reviewed by our customer service team and responded to within a reasonable timeframe based on the nature and complexity of the issue.",
          "PTC reserves the right to request additional evidence, conduct inspections, review service records, and communicate directly with the attending operatives before reaching a final decision regarding any complaint or claim.",
        ],
      },
    ],
  },
  {
    id: "final",
    number: "7",
    title: "Final Notes",
    subsections: [
      {
        id: "amendments",
        title: "7.1 Amendments",
        paragraphs: [
          "These Terms and Conditions may be updated, amended, or modified by PTC at any time without prior notice.",
          "The most current version of these Terms and Conditions shall always be available on our website at primetenancycleaning.com/T-And-C.",
          "Continued use of our website or services following any updates or amendments shall constitute acceptance of the revised Terms and Conditions.",
        ],
      },
      {
        id: "contact-us",
        title: "7.2 Contact",
        paragraphs: [
          "For any questions, concerns, or enquiries relating to these Terms and Conditions, customers may contact PTC using the contact details provided on our website.",
        ],
      },
    ],
  },
];

// ============================================================================
// SECTION RENDERER
// ============================================================================

const SubsectionBlock = ({ sub }) => (
  <div id={sub.id} className="scroll-mt-28">
    <h3 className="font-heading text-xl md:text-2xl font-semibold text-navy mb-4">
      {sub.title}
    </h3>
    <div className="space-y-3 text-slate-700 leading-relaxed text-sm md:text-base">
      {sub.paragraphs?.map((p, i) => (
        <p key={`p-${i}`}>{p}</p>
      ))}

      {sub.lists?.map((list, li) => (
        <div key={`list-${li}`} className="space-y-3 pt-1">
          {list.heading && <p className="font-medium text-navy">{list.heading}</p>}
          <ul className="space-y-2 pl-1">
            {list.items.map((item, ii) => (
              <li
                key={`item-${ii}`}
                className="flex items-start gap-2.5"
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {list.footer && <p>{list.footer}</p>}
        </div>
      ))}

      {sub.trailing?.map((p, i) => (
        <p key={`trail-${i}`}>{p}</p>
      ))}
    </div>
  </div>
);

const SectionBlock = ({ section }) => (
  <motion.section
    id={section.id}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.4 }}
    className="scroll-mt-28"
    data-testid={`terms-section-${section.id}`}
  >
    <div className="flex items-baseline gap-4 mb-8 pb-4 border-b border-slate-200">
      <span className="font-heading text-3xl md:text-4xl font-bold text-gold-dark">
        {section.number}
      </span>
      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy">
        {section.title}
      </h2>
    </div>

    <div className="space-y-10">
      {section.subsections.map((sub) => (
        <SubsectionBlock key={sub.id} sub={sub} />
      ))}
    </div>
  </motion.section>
);

// ============================================================================
// TABLE OF CONTENTS (sticky desktop)
// ============================================================================

const TableOfContents = () => {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  useEffect(() => {
    const handle = () => {
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top < 160) current = s.id;
      }
      setActiveId(current);
    };
    handle();
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, []);

  return (
    <nav
      aria-label="Terms sections"
      className="sticky top-28"
      data-testid="terms-toc"
    >
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
        Contents
      </p>
      <ul className="space-y-1.5">
        {SECTIONS.map((s) => {
          const active = s.id === activeId;
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className={`flex items-baseline gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active
                    ? "bg-navy text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-navy"
                }`}
                data-testid={`terms-toc-${s.id}`}
              >
                <span
                  className={`font-heading font-bold text-xs w-4 ${
                    active ? "text-gold" : "text-slate-400"
                  }`}
                >
                  {s.number}
                </span>
                <span className="leading-tight">{s.title}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

// ============================================================================
// PAGE
// ============================================================================

const Terms = () => {
  return (
    <>
      <Seo
        title="Terms & Conditions"
        description="Read the Prime Tenancy Cleaning London terms and conditions covering bookings, pricing, cancellations, refunds, guarantees, liability, and data protection."
        ogTitle="Terms & Conditions | Prime Tenancy Cleaning"
        ogDescription="The terms governing bookings, pricing, refunds, guarantees and liability for Prime Tenancy Cleaning London."
        path={PATH}
        schema={[
          breadcrumbSchema([{ name: "Terms & Conditions", path: PATH }]),
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Terms & Conditions",
            url: "https://primetenancycleaning.com/terms",
          },
        ]}
      />

      <PageHero
        eyebrow="Legal"
        h1="Terms &"
        highlight="Conditions"
        subheading="The terms governing the use of our website and the cleaning services provided by Prime Tenancy Cleaning across London. Please read carefully before booking."
        breadcrumbs={[{ name: "Terms & Conditions", path: PATH }]}
        image="https://images.unsplash.com/photo-1521012108593-cf7f7c0fbb89?w=1920&q=80"
        trustBadges={false}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          {/* Header card */}
          <div className="max-w-5xl mx-auto mb-12 bg-slate-50 border border-slate-100 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="font-heading text-lg font-semibold text-navy">
                  Website Terms and Conditions
                </p>
                <p className="text-slate-500 text-sm flex items-center gap-1.5 mt-0.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Last updated: {LAST_UPDATED}
                </p>
              </div>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed sm:max-w-md">
              By making a booking or using our website you agree to these
              Terms and Conditions in full. They apply to all customers,
              services and communications.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 max-w-6xl mx-auto">
            <aside className="hidden lg:block lg:col-span-3">
              <TableOfContents />
            </aside>

            <article className="lg:col-span-9 space-y-16">
              {SECTIONS.map((s) => (
                <SectionBlock key={s.id} section={s} />
              ))}

              {/* Closing CTA */}
              <div className="bg-navy rounded-2xl p-8 md:p-10 text-white">
                <h3 className="font-heading text-xl md:text-2xl font-semibold mb-3">
                  Questions about these terms?
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Get in touch using the contact details below or via our
                  contact page and a member of our London team will respond
                  within one working day.
                </p>
                <div className="flex flex-wrap gap-3 text-sm font-semibold">
                  <Link
                    to="/contact"
                    className="bg-gold hover:bg-gold-dark text-navy px-5 py-3 rounded-xl transition-colors"
                  >
                    Contact us
                  </Link>
                  <a
                    href="tel:+442080363586"
                    className="border border-white/30 text-white hover:bg-white/10 px-5 py-3 rounded-xl transition-colors inline-flex items-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    020 8036 3586
                  </a>
                  <a
                    href="mailto:info@primetenancycleaning.com"
                    className="border border-white/30 text-white hover:bg-white/10 px-5 py-3 rounded-xl transition-colors inline-flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    info@primetenancycleaning.com
                  </a>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    </>
  );
};

export default Terms;
